import { SaleProduct, Variant } from './types';

/**
 * Collapses products the catalogue has grouped into a single tile.
 *
 * A publisher sometimes sells the same magazine as several products, one per term — a
 * 1-year row and a 2-year row — and `grouped_product_ids` is how the catalogue says so.
 * The sale should show one tile whose Term dropdown spans all of them, not three tiles
 * that look like duplicates.
 *
 * The first product of a group keeps the tile; the rest are absorbed and drop out of
 * the list. A group is only formed when the leader actually has a Term variant to
 * extend, because a group with nothing to choose between is just a duplicate.
 *
 * Ported from the legacy helper's behaviour, with one correction. That version tested
 * `if (!products.findIndex(...))`, which is falsy for **index 0** as well as for the -1
 * that means "not found" — so a grouped product that happened to sit first in the
 * category was silently dropped from its own group, and a missing one was merged as
 * `products[-1]`, i.e. `undefined`. Both are `=== -1` here.
 */
export default function groupProducts(products: SaleProduct[] | undefined): SaleProduct[] | null {
  if (!products) {
    return null;
  }

  const merged: SaleProduct[] = [];
  const absorbed = new Set<number>();

  products.forEach((original) => {
    if (absorbed.has(original.id)) {
      return;
    }

    if (!original.grouped_product_ids || original.grouped_product_ids.length <= 1) {
      merged.push(original);
      return;
    }

    if (!original.variants.find((variant) => variant.name === 'Term')) {
      merged.push(original);
      return;
    }

    const product: SaleProduct = { ...original, grouped_products: [] };
    product.grouped_products = [product];
    merged.push(product);

    original.grouped_product_ids.forEach((id) => {
      if (id === product.id) {
        return;
      }

      const index = products.findIndex((candidate) => candidate.id === id);

      if (index === -1) {
        return;
      }

      product.grouped_products!.push({ ...products[index] });
      absorbed.add(id);
    });
  });

  return merged;
}

/**
 * The variants a grouped tile offers: every member's options, unioned per variant.
 *
 * The Term list is the point of it — the tile has to offer 1 year *and* 2 years even
 * though each is a different product — and `restricted_terms` is re-derived from the
 * union so that selecting a term and pricing it read from the same list.
 */
export function getProductWithMergedVariants(
  product: SaleProduct,
  groupedProducts: SaleProduct[] | undefined
): SaleProduct {
  if (!groupedProducts || groupedProducts.length === 0) {
    return product;
  }

  let variants: Variant[] = [];

  groupedProducts.forEach((groupedProduct, index) => {
    variants = index === 0 ? groupedProduct.variants : mergeVariants(variants, groupedProduct.variants);
  });

  const term = variants.find((variant) => variant.name === 'Term');

  return { ...product, variants, restricted_terms: term ? term.options : product.restricted_terms };
}

function mergeVariants(a: Variant[], b: Variant[] | undefined): Variant[] {
  if (!b || b.length === 0) {
    return a;
  }

  if (!a || a.length === 0) {
    return b;
  }

  return a.map((variantA) => {
    const variantB = b.find((candidate) => candidate.name === variantA.name);

    if (!variantB || (variantB.options || []).length === 0) {
      return variantA;
    }

    return { ...variantA, options: (variantA.options || []).concat(variantB.options) };
  });
}

/**
 * Which member of a group actually sells the chosen term.
 *
 * The tile shows one product but the add-to-cart has to post the id of the row that
 * owns that term, so every price, image and form on the tile follows the selection
 * through here.
 */
export function selectProductForTerm(
  product: SaleProduct,
  selectedTermId: string | number | null
): SaleProduct {
  if (!product.grouped_products || product.grouped_products.length === 0 || selectedTermId === null) {
    return product;
  }

  const owner = product.grouped_products.find((candidate) =>
    (candidate.restricted_terms || []).some((term) => String(term.id) === String(selectedTermId))
  );

  return owner ?? product;
}
